import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, Copy, Plus, Trash2, XCircle, ShieldCheck } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "technician", label: "ADAS Technician" },
  { value: "sales", label: "Sales Rep" },
  { value: "finance", label: "Finance" },
  { value: "reception", label: "Reception" },
  { value: "manager", label: "Manager" },
];

export default function AdminInviteCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/invite-codes");
      setCodes(r.data || []);
    } catch (e) {
      toast.error("Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copyCode = async (c) => {
    try { await navigator.clipboard.writeText(c); toast.success("Code copied"); } catch (e) { toast.error("Copy failed"); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this code? Anyone using it will fail to register.")) return;
    try {
      await api.post(`/admin/invite-codes/${id}/revoke`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error("Revoke failed"); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-blue-400 mb-2 flex items-center gap-1.5">
            <KeyRound className="w-3 h-3" /> ADMIN
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Invite codes</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">
            Generate codes and share only with people you want to give access. Users cannot register without a valid code.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="bg-blue-600 hover:bg-blue-500"
          data-testid="create-invite-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Generate new code
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
        </div>
      ) : codes.length === 0 ? (
        <Card className="bg-[#0B1120] border-slate-800 p-12 text-center">
          <KeyRound className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <h3 className="font-display text-lg text-slate-300 mb-1">No invite codes yet</h3>
          <p className="text-sm text-slate-500 mb-4">Generate one to start onboarding your team.</p>
          <Button onClick={() => setCreating(true)} className="bg-blue-600 hover:bg-blue-500">
            <Plus className="w-4 h-4 mr-2" /> Generate your first code
          </Button>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="invite-codes-list">
          {codes.map((c) => {
            const isExpired = c.expires_at && new Date(c.expires_at) < new Date();
            const isMaxed = c.max_uses && c.uses_count >= c.max_uses;
            const isRevoked = c.revoked;
            const active = !isExpired && !isMaxed && !isRevoked;
            return (
              <Card key={c.id} className="bg-[#0B1120] border-slate-800 p-4" data-testid={`invite-${c.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="font-mono text-base font-bold text-blue-300 select-all" data-testid={`invite-code-${c.id}`}>{c.code}</code>
                      {active ? (
                        <Badge className="bg-emerald-500/15 border-emerald-500/40 text-emerald-300 text-[10px] uppercase">Active</Badge>
                      ) : isRevoked ? (
                        <Badge className="bg-rose-500/15 border-rose-500/40 text-rose-300 text-[10px] uppercase">Revoked</Badge>
                      ) : isExpired ? (
                        <Badge className="bg-slate-500/15 border-slate-500/40 text-slate-400 text-[10px] uppercase">Expired</Badge>
                      ) : (
                        <Badge className="bg-amber-500/15 border-amber-500/40 text-amber-300 text-[10px] uppercase">Maxed</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">{c.label} · Suggested role: <b className="text-slate-300">{c.suggested_role}</b></div>
                    <div className="text-[10px] text-slate-600 mt-1">
                      Uses: {c.uses_count}{c.max_uses ? `/${c.max_uses}` : " (unlimited)"} · Created {new Date(c.created_at).toLocaleDateString()}
                      {c.expires_at && <> · Expires {new Date(c.expires_at).toLocaleDateString()}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyCode(c.code)}
                      className="border-slate-700"
                      data-testid={`copy-invite-${c.id}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    {active && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revoke(c.id)}
                        className="border-rose-500/40 text-rose-300"
                        data-testid={`revoke-invite-${c.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {creating && (
        <CreateInviteDialog
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateInviteDialog({ onClose, onDone }) {
  const [form, setForm] = useState({
    label: "", suggested_role: "technician", max_uses: "", expires_in_days: "30",
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/invite-codes", {
        label: form.label.trim() || null,
        suggested_role: form.suggested_role,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        expires_in_days: form.expires_in_days ? parseInt(form.expires_in_days, 10) : null,
      });
      setCreated(r.data);
    } catch (e) {
      toast.error("Create failed");
    } finally {
      setSaving(false);
    }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(created.code); toast.success("Copied!"); } catch (e) {}
  };

  return (
    <Dialog open onOpenChange={(o) => !o && (created ? onDone() : onClose())}>
      <DialogContent className="bg-[#0B1120] border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-400" /> {created ? "Code generated!" : "New invite code"}
          </DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-4 mt-2" data-testid="invite-created">
            <div className="rounded-xl border border-blue-500/40 bg-blue-500/5 p-6 text-center">
              <code className="font-mono text-2xl font-bold text-blue-300 select-all break-all" data-testid="new-invite-code">{created.code}</code>
              <p className="text-[10px] text-slate-500 mt-3">Share this with the person you want to invite. They enter it during registration.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={copyCode} className="flex-1 bg-blue-600 hover:bg-blue-500"><Copy className="w-4 h-4 mr-2" /> Copy code</Button>
              <Button variant="outline" onClick={onDone} className="border-slate-700">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-slate-400">Label (optional)</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="invite-label"
                placeholder="e.g. New tech João"
                className="bg-slate-950 border-slate-800 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Suggested role</Label>
              <select
                value={form.suggested_role}
                onChange={(e) => setForm({ ...form, suggested_role: e.target.value })}
                data-testid="invite-role"
                className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm mt-1"
              >
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Max uses</Label>
                <Input inputMode="numeric" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  data-testid="invite-max-uses"
                  placeholder="Unlimited"
                  className="bg-slate-950 border-slate-800 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Expires in (days)</Label>
                <Input inputMode="numeric" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
                  data-testid="invite-expires"
                  placeholder="30"
                  className="bg-slate-950 border-slate-800 mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
              <Button variant="outline" onClick={onClose} className="border-slate-700">Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="invite-generate-btn">
                {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</> : <>Generate code</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
